<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { mdiClose } from '@quasar/extras/mdi-v7';
import { useStrapiPost } from 'shared/composables/useStrapiApi';
import useValidators from 'src/shared/composables/useValidators';
import BButton from 'src/shared/components/base/BButton/BButton.vue';
import BInput from 'src/shared/components/base/BInput/BInput.vue';

const { t: $t } = useI18n();
const route = useRoute();
const router = useRouter();
const $q = useQuasar();
const { required, min } = useValidators();

// See SchoolRegisterSignInView: dark ink on the purple/white Flash palette.
const FIELD_SHADOW = '#1c1c1c';

const password = ref('');
const passwordConfirmation = ref('');
const isSubmitting = ref(false);

const code = computed(() => (route.query.code as string) || '');

const { execute, error } = useStrapiPost('auth/reset-password' as never);

async function onSubmit() {
  if (!code.value) {
    $q.notify({ type: 'negative', message: $t('RESET_CODE_MISSING') });
    return;
  }
  isSubmitting.value = true;
  try {
    await execute({
      data: {
        password: password.value,
        passwordConfirmation: passwordConfirmation.value,
        code: code.value,
      },
    });

    if (error.value) {
      const msg = (error.value as { message?: string })?.message;
      $q.notify({
        type: 'negative',
        message: msg || $t('FAILED_TO_RESET_PASSWORD'),
      });
      return;
    }

    $q.notify({ type: 'positive', message: $t('PASSWORD_RESET_SUCCESSFUL') });
    await router.push({ name: 'school.login' });
  } catch (err) {
    console.error($t('RESET_PASSWORD_ERROR'), err);
    $q.notify({
      type: 'negative',
      message: $t('FAILED_TO_RESET_PASSWORD'),
    });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <q-card
    square
    flat
    class="full-width bg-transparent text-dark flash-auth-card"
  >
    <q-card-section class="column items-center q-pb-none">
      <q-img
        src="/img/flashLogo_dark.svg"
        no-spinner
        alt="FlashStudies"
        class="flash-auth-logo"
      />
      <h3
        class="text-h4 q-my-md q-px-sm text-center text-uppercase text-weight-bold"
      >
        {{ $t('RESET_PASSWORD') }}
      </h3>
    </q-card-section>

    <q-card-section
      class="q-mx-auto full-width"
      style="max-width: 400px"
    >
      <q-form
        class="q-px-sm q-pt-md q-gutter-y-lg"
        @submit.prevent="onSubmit"
      >
        <BInput
          v-model="password"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          type="password"
          lazy-rules
          :rules="[required, (val: string) => min(val, 6)]"
          :label="$t('PASSWORD')"
          autocomplete="new-password"
          :shadow-color="FIELD_SHADOW"
          label-color="dark"
          class="bg-white"
        />

        <BInput
          v-model="passwordConfirmation"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          type="password"
          lazy-rules
          :rules="[
            required,
            (val: string) => val === password || $t('PASSWORDS_MUST_MATCH'),
          ]"
          :label="$t('CONFIRM_NEW_PASSWORD')"
          autocomplete="new-password"
          :shadow-color="FIELD_SHADOW"
          label-color="dark"
          class="bg-white"
        />

        <BButton
          unelevated
          color="secondary"
          :loading="isSubmitting"
          text-color="dark"
          :shadow-color="FIELD_SHADOW"
          class="full-width"
          :label="$t('RESET_PASSWORD')"
          type="submit"
        />
      </q-form>
    </q-card-section>

    <q-card-section class="row items-center justify-center">
      <q-btn
        flat
        size="sm"
        class="q-my-none q-py-none"
        color="dark"
        :to="{ name: 'school.login' }"
        :label="$t('BACK_TO_LOGIN')"
      />
    </q-card-section>
  </q-card>
</template>

<style scoped>
.flash-auth-card {
  /* Stack above the fixed decoration art. */
  position: relative;
  z-index: 1;
}

.flash-auth-logo {
  width: 11rem;
  max-width: 60vw;
}
</style>
